import { useState } from 'react';

import { Duplicate as DuplicateIcon } from '@/components/chariot-ui';
import { Tooltip } from '@/components/Tooltip';
import { Section } from '@/sections/threats/components';
import type { FrameworkRef } from '@/sections/threats/types';
import {
  DEFAULT_BADGE_COLOR,
  FRAMEWORK_BADGE_COLORS,
  frameworkDisplayName,
} from '@/sections/vulnerabilities/utils/frameworkEnrichment.util';
import { cn } from '@/utils/classname';
import { copyToClipboard } from '@/utils/copyToClipboard.util';

interface FrameworkRefsComponentProps {
  frameworkRefs: FrameworkRef[];
}

function CopyUrl({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <button
      type="button"
      className="text-chariot-brand-primary flex w-full items-center gap-1 text-[10px] hover:underline"
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
        copyToClipboard(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
    >
      <DuplicateIcon className="size-3 shrink-0" />
      <span className="truncate">{copied ? 'Copied!' : url}</span>
    </button>
  );
}

function BadgeTooltipContent({ data }: { data: FrameworkRef }) {
  return (
    <div className="flex max-w-xs flex-col gap-1.5">
      <div className="text-chariot-text-secondary text-[10px] font-medium tracking-wide uppercase">
        {frameworkDisplayName(data.framework)}
      </div>
      <div className="text-chariot-text-primary text-xs font-semibold">
        {data.id}
      </div>
      {data.name && (
        <div className="text-chariot-text-secondary text-xs">{data.name}</div>
      )}
      {data.url && <CopyUrl url={data.url} />}
    </div>
  );
}

export function FrameworkRefsComponent({
  frameworkRefs,
}: FrameworkRefsComponentProps) {
  if (frameworkRefs.length === 0) return null;

  const grouped = new Map<string, FrameworkRef[]>();
  for (const item of frameworkRefs) {
    const existing = grouped.get(item.framework) ?? [];
    existing.push(item);
    grouped.set(item.framework, existing);
  }

  return (
    <Section title="Security Frameworks">
      <div className="flex flex-col gap-4">
        {[...grouped.entries()].map(([framework, refs]) => (
          <div key={framework} className="flex flex-col gap-2">
            <h5 className="text-chariot-text-secondary text-xs font-semibold tracking-wide uppercase">
              {frameworkDisplayName(framework)}
            </h5>
            <div className="flex flex-wrap gap-1.5">
              {refs.map(fwRef => {
                const color =
                  FRAMEWORK_BADGE_COLORS[framework] ?? DEFAULT_BADGE_COLOR;
                const label = fwRef.name
                  ? `${fwRef.id}: ${fwRef.name}`
                  : fwRef.id;

                const badge = fwRef.url ? (
                  <a
                    key={fwRef.id}
                    href={fwRef.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'inline-flex items-center rounded border px-2 py-0.5 text-xs font-semibold transition-opacity hover:opacity-80',
                      color
                    )}
                  >
                    {label}
                  </a>
                ) : (
                  <span
                    key={fwRef.id}
                    className={cn(
                      'inline-flex items-center rounded border px-2 py-0.5 text-xs font-semibold',
                      color
                    )}
                  >
                    {label}
                  </span>
                );

                return (
                  <Tooltip
                    key={fwRef.id}
                    title={<BadgeTooltipContent data={fwRef} />}
                  >
                    {badge}
                  </Tooltip>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}
